import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from 'src/config';
import { EffectedRoutesOnStop, LineSearchResult, StopArea, StopPlace, StopSearchResult } from '@components/traffic-deviations/components/searches/components/search-stop/interfaces';
import { LineSearchStopArea } from '@components/traffic-deviations/components/searches/components/search-stop/interfaces/line-search-result';
import { firstValueFrom, map } from 'rxjs';
import { NotificationService } from '@modules/shared/services/notification.service';
import { GetRoutesByPlaceIdResponse, GetStopsByRouteIdResponse, PostRoutesByLineResponse, PostStopsResponse, RoutesByPlaceId } from './interfaces';
import { LoggingService } from '@modules/shared/services/logging.service';

@Injectable({
  providedIn: 'root'
})
export class MapApiService {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(ConfigService);
  private readonly _loggingService = inject(LoggingService)
  private readonly _notificationService = inject(NotificationService);
  
  public async postRoutesByLine(
    line: string,
    hastusRegions: string[],
    startDateTime: string,
    endDateTime: string
  ): Promise<LineSearchResult[]> {
    const url = `${this._config.mapApiBaseUrl}/v1/Routes/ByLine`;
    const data = { line, hastusRegions, startDateTime, endDateTime };

    try {
      return await firstValueFrom(this._http.post(url, data).pipe(
        map((response: PostRoutesByLineResponse) => response.routes),
        map((routes) => routes.map((r) => {
          const route: LineSearchResult = {
            ...r,
            searchType: 'LineSearch'
          };

          return route;
        }))
      ));
    } catch (error) {
      if (error.status === 404) {
        const message = `OMS.MAP.Api - postRoutesByLine, routes not found`;
        this._notificationService.openText(message);
        console.error(message, error);
      } else {
        const message = `OMS.MAP.Api - postRoutesByLine, failed to fetch routes`;
        this._notificationService.openText(message);
        console.error(message, error);
      }

      return [];
    }
  }

  public async postStops(
    stopDescription: string,
    hastusRegions: string[],
    startDateTime: string,
    endDateTime: string
  ): Promise<StopSearchResult[]> {
    const url = `${this._config.mapApiBaseUrl}/v1/Stops`;
    const data = { stopDescription, hastusRegions, startDateTime, endDateTime };

    try {
      return await firstValueFrom(this._http.post(url, data).pipe(
        map((response: PostStopsResponse) => response.stops),
        map((stops) => {
            return stops.map((s) => {
              if (s.type === 'stopArea') {
                const stopArea: StopArea = {
                  type: 'StopArea',
                  searchType: 'StopSearch',
                  placeId: s.id,
                  description: s.description,
                  hastusRegion: s.hastusRegion
                };

                return stopArea;
              } 

              if (!s.parentId) {
                this._loggingService.logTrace(`OMS.MAP.Api - postStops, stopPlace without parentId`);
              }
        
              const stopPlace: StopPlace = {
                type: 'StopPlace',
                searchType: 'StopSearch',
                placeId: s.id,
                parentId: s.parentId,
                description: s.description,
                platformCode: s.platformCode,
                hastusRegion: s.hastusRegion,
                placeLatitude: s.latitude,
                placeLongitude: s.longitude,
                replaceName: ''
              };

              return stopPlace;
            });          
        })
      ));
    } catch (error) {
      if (error.status === 404) {
        const message = `OMS.MAP.Api - postStops, stops not found`;
        this._notificationService.openText(message);
        console.error(message, error);
      } else {
        const message = `OMS.MAP.Api - postStops, failed to fetch stops`;
        this._notificationService.openText(message);
        console.error(message, error);
      }

      return [];
    }
  }

  public async getRoutesByPlaceId(
    placeId: string, 
    hastusRegion: string,
    startTime: string,
    endTime: string
  ): Promise<RoutesByPlaceId[]> {
    const url = `${this._config.mapApiBaseUrl}/v1/Routes/${hastusRegion}/ByPlaceId?placeId=${placeId}&startTime=${encodeURIComponent(startTime)}&endTime=${encodeURIComponent(endTime)}`;

    try {
      return await firstValueFrom(this._http.get(url).pipe(
        map((response: GetRoutesByPlaceIdResponse) => response.routes)
      ));
    } catch (error) {
      if (error.status === 404) {
        const message = `OMS.MAP.Api - getRoutesByPlaceId, placeId was not found`;
        this._notificationService.openText(message);
        console.error(message, error);
      } else {
        const message = `OMS.MAP.Api - getRoutesByPlaceId, failed to fetch routes`;
        this._notificationService.openText(message);
        console.error(message, error);
      }
    }

    return [];
  }

  public async getStopsByRouteId(
    routeId: string, 
    hastusRegion: string, 
    startDateTime: string, 
    endDateTime: string,
    direction?: number,
    templateTripId?: string
  ): Promise<LineSearchStopArea[]> {
    try {
      const params: {[key:string]: string} = {};
      if (direction) params.direction = direction.toString();

      params.startTime = startDateTime;
      params.endTime = endDateTime;
      if (templateTripId) params.templateTripId = templateTripId;

      let url = `${this._config.mapApiBaseUrl}/v1/Stops/${hastusRegion}/${routeId}`;

      return await firstValueFrom(this._http.get(url, { params: params }).pipe(
        map((response: GetStopsByRouteIdResponse) => response.places),
        map((places) => {
          const lineSearchStopAreas: LineSearchStopArea[] = places.map((p) => {
            return {
              ...p,
              stopPlaces: p.stopPlaces.map((stopPlace) => {
                return { 
                  ...stopPlace, 
                  parentId: p.stopAreaId,
                  hastusRegion: p.hastusRegion 
                };
              })
            };
          });

          return lineSearchStopAreas;
        })
      ));
    } catch (error) {
      if (error.status === 404) {
        const message = `OMS.MAP.Api - getStopsByRouteId, stops not found`;
        this._notificationService.openText(message);
        console.error(message, error);
      } else {
        const message = `OMS.MAP.Api - getStopsByRouteId, failed to fetch stops`;
        this._notificationService.openText(message);
        console.error(message, error);
      }

      return [];
    }
  }

  public async getTripByRouteId(
    routeId: string, 
    hastusRegion: string,
    direction: string, 
    startDateTime: string,
    endDateTime: string,
    templateTripId?: string
  ) {
    try {
      let url = `${this._config.mapApiBaseUrl}/v1/Trip/${hastusRegion}/${routeId}?direction=${direction}&startTime=${encodeURIComponent(startDateTime)}&endTime=${encodeURIComponent(endDateTime)}`;

      if (templateTripId) url += `&templateTripId=${templateTripId}`;

      return await firstValueFrom(this._http.get(url).pipe(
        map((response) => response as EffectedRoutesOnStop[])
      ));
    } catch (error) {
      if (error.status === 404) {
        const message = `OMS.MAP.Api - getTripByRouteId, routeId was not found`;
        this._notificationService.openText(message);
        console.error(message, error);
      } else {
        const message = `OMS.MAP.Api - getTripByRouteId, failed to fetch trips`;
        this._notificationService.openText(message);
        console.error(message, error);
      }

      return [];
    }
  }

  /**
   * Returns all trip numbers for a specific route and direction.
   * Used to reliably determine which side of the line view a vehicle belongs to.
   */
  public async getTripsByRoute(
    routeId: string,
    hastusRegion: string,
    direction: number,
    startDateTime: string,
    endDateTime: string,
    templateTripId?: string
  ): Promise<string[]> {
    try {
      let url = `${this._config.mapApiBaseUrl}/v1/Trip/${hastusRegion}/${routeId}?direction=${direction}&startTime=${encodeURIComponent(startDateTime)}&endTime=${encodeURIComponent(endDateTime)}`;
      if (templateTripId) url += `&templateTripId=${templateTripId}`;
      const trips = await firstValueFrom(
        this._http.get<{ tripNumber: string }[]>(url)
      );
      return (trips || []).map(t => t.tripNumber).filter(n => !!n);
    } catch {
      return [];
    }
  }
}
